import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronLeft, Plus, Trash2, CheckCircle2, XCircle, Loader2, Globe, Stethoscope, X } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  name: '', url: '', username: '', app_password: '',
  default_author_id: '', default_author_name: '',
  default_category_id: '', default_category_name: '',
  default_status: 'publish',
  default_categories_count: 2,
  // Editorial rules per media
  prompt_template: '',
  tone: '',
  paragraphs_min: 3,
  paragraphs_max: 5,
  title_style: '',
  additional_instructions: '',
  show_sources: true,
  freshness_days: 15,
};

export default function SitesSettings() {
  const [sites, setSites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [diagnosing, setDiagnosing] = useState({});
  const [diagReport, setDiagReport] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/wordpress-sites');
      setSites(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setAuthors([]); setCategories([]);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setForm({
      name: s.name, url: s.url, username: s.username, app_password: '',
      default_author_id: s.default_author_id || '', default_author_name: s.default_author_name || '',
      default_category_id: s.default_category_id || '', default_category_name: s.default_category_name || '',
      default_status: s.default_status || 'publish',
      default_categories_count: s.default_categories_count || 2,
      prompt_template: s.prompt_template || '',
      tone: s.tone || '',
      paragraphs_min: s.paragraphs_min || (s.paragraphs ? Math.max(3, s.paragraphs - 1) : 3),
      paragraphs_max: s.paragraphs_max || s.paragraphs || 5,
      title_style: s.title_style || '',
      additional_instructions: s.additional_instructions || '',
      show_sources: s.show_sources !== false,
      freshness_days: s.freshness_days || 15,
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    // Validate min <= max
    const pMin = Number(form.paragraphs_min) || 3;
    const pMax = Number(form.paragraphs_max) || 5;
    if (pMin > pMax) {
      toast.error('Le minimum de paragraphes doit être ≤ au maximum');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_author_id: form.default_author_id ? Number(form.default_author_id) : null,
        default_category_id: form.default_category_id ? Number(form.default_category_id) : null,
        default_categories_count: Number(form.default_categories_count) || 2,
        paragraphs_min: pMin,
        paragraphs_max: pMax,
        paragraphs: pMax,  // legacy fallback
        freshness_days: Number(form.freshness_days) || 15,
      };
      if (editingId) {
        await api.patch(`/wordpress-sites/${editingId}`, payload);
        toast.success('Média mis à jour');
      } else {
        await api.post('/wordpress-sites', payload);
        toast.success('Média ajouté');
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce média ?')) return;
    try {
      await api.delete(`/wordpress-sites/${id}`);
      toast.success('Média supprimé');
      await load();
    } catch (e) { toast.error('Échec'); }
  };

  const test = async (id) => {
    setTesting((t) => ({ ...t, [id]: true }));
    try {
      const { data } = await api.post(`/wordpress-sites/${id}/test`);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) { toast.error('Connexion impossible'); }
    finally { setTesting((t) => ({ ...t, [id]: false })); }
  };

  const diagnose = async (id) => {
    setDiagnosing((t) => ({ ...t, [id]: true }));
    try {
      const { data } = await api.post(`/wordpress-sites/${id}/diagnose`);
      setDiagReport(data);
    } catch (e) {
      toast.error('Diagnostic échoué : ' + (e?.response?.data?.detail || 'erreur inconnue'));
    } finally {
      setDiagnosing((t) => ({ ...t, [id]: false }));
    }
  };

  const lookupAuthorsAndCats = async () => {
    // Save site first (if new) to test, else use existing id
    if (!form.url || !form.username || !form.app_password) {
      toast.error('Remplissez d\'abord URL, identifiant et clé');
      return;
    }
    setLookupLoading(true);
    try {
      let siteId = editingId;
      if (!siteId) {
        const payload = {
          ...form,
          default_author_id: form.default_author_id ? Number(form.default_author_id) : null,
          default_category_id: form.default_category_id ? Number(form.default_category_id) : null,
        };
        const { data } = await api.post('/wordpress-sites', payload);
        siteId = data.id;
        setEditingId(data.id);
        toast.success('Site enregistré, récupération des auteurs/catégories…');
      }
      const [aRes, cRes] = await Promise.all([
        api.get(`/wordpress-sites/${siteId}/authors`),
        api.get(`/wordpress-sites/${siteId}/categories`),
      ]);
      setAuthors(aRes.data); setCategories(cRes.data);
      toast.success('Auteurs & catégories chargés');
    } catch (e) {
      toast.error('Impossible de récupérer. Vérifiez les identifiants.');
    } finally { setLookupLoading(false); }
  };

  return (
    <div className="mobile-shell pb-8">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav('/settings')} className="flex items-center gap-1 text-slate-700 font-medium" data-testid="btn-back">
          <ChevronLeft size={22} /> Retour
        </button>
        <h1 className="font-bold text-slate-900">Médias</h1>
        <button onClick={openNew} data-testid="btn-add-site" className="text-blue-600 font-semibold flex items-center gap-1">
          <Plus size={18} />
        </button>
      </header>

      <div className="px-5 pt-5 space-y-3">
        {loading && <Loader2 className="mx-auto animate-spin text-blue-600" />}
        {!loading && sites.length === 0 && !showForm && (
          <div className="py-16 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <Globe className="text-blue-600" />
            </div>
            <p className="text-slate-700 font-semibold">Aucun site configuré</p>
            <button onClick={openNew} className="mt-4 bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl" data-testid="btn-add-first-site">
              + Ajouter un site WordPress
            </button>
          </div>
        )}

        {sites.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`site-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500 truncate max-w-[220px]">{s.url}</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {s.default_author_name && <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">Auteur : {s.default_author_name}</span>}
                  {s.default_category_name && <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{s.default_category_name}</span>}
                  <span className="text-xs px-2 py-0.5 rounded-md bg-blue-50 text-blue-700">{s.default_status === 'publish' ? 'Publier' : 'Brouillon'}</span>
                </div>
              </div>
              <button onClick={() => remove(s.id)} data-testid={`btn-delete-site-${s.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                <Trash2 size={16} />
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => test(s.id)} disabled={testing[s.id]} data-testid={`btn-test-site-${s.id}`}
                className="flex-1 border-2 border-slate-200 py-2 rounded-xl text-sm font-semibold hover:bg-slate-50 flex items-center justify-center gap-1">
                {testing[s.id] ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Tester
              </button>
              <button onClick={() => diagnose(s.id)} disabled={diagnosing[s.id]} data-testid={`btn-diagnose-site-${s.id}`}
                className="flex-1 border-2 border-amber-200 bg-amber-50 py-2 rounded-xl text-sm font-semibold text-amber-800 hover:bg-amber-100 flex items-center justify-center gap-1">
                {diagnosing[s.id] ? <Loader2 size={14} className="animate-spin" /> : <Stethoscope size={14} />}
                Diagnostic
              </button>
              <button onClick={() => openEdit(s)} data-testid={`btn-edit-site-${s.id}`}
                className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-sm font-semibold">
                Modifier
              </button>
            </div>
          </div>
        ))}

        {showForm && (
          <form onSubmit={save} className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 mt-4" data-testid="site-form">
            <h2 className="font-bold text-slate-900">{editingId ? 'Modifier le média' : 'Nouveau média'}</h2>
            <Field label="Nom du média" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="site-field-name" placeholder="Opinion Internationale" />
            <Field label="URL du site WordPress" value={form.url} onChange={(v) => setForm({ ...form, url: v })} required testid="site-field-url" placeholder="https://monsite.com" type="url" />
            <Field label="Identifiant WordPress" value={form.username} onChange={(v) => setForm({ ...form, username: v })} required testid="site-field-username" placeholder="utilisateur" />
            <Field label="Clé d'application" value={form.app_password} onChange={(v) => setForm({ ...form, app_password: v })} required={!editingId} testid="site-field-password" placeholder={editingId ? '(laisser vide pour conserver)' : 'xxxx xxxx xxxx xxxx'} type="password" />

            <button type="button" onClick={lookupAuthorsAndCats} disabled={lookupLoading}
              className="w-full border-2 border-blue-200 text-blue-700 font-semibold py-2.5 rounded-xl hover:bg-blue-50 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="btn-lookup-wp">
              {lookupLoading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Récupérer auteurs & catégories
            </button>

            {authors.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Auteur par défaut</label>
                <select value={form.default_author_id} onChange={(e) => {
                  const id = e.target.value;
                  const a = authors.find(x => String(x.id) === id);
                  setForm({ ...form, default_author_id: id, default_author_name: a?.name || '' });
                }} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-testid="site-field-author">
                  <option value="">— Choisir —</option>
                  {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}

            {categories.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Catégorie par défaut</label>
                <select value={form.default_category_id} onChange={(e) => {
                  const id = e.target.value;
                  const c = categories.find(x => String(x.id) === id);
                  setForm({ ...form, default_category_id: id, default_category_name: c?.name || '' });
                }} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-testid="site-field-category">
                  <option value="">— Choisir —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Statut par défaut</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, default_status: 'draft' })}
                  className={`flex-1 py-2.5 rounded-xl font-semibold border-2 ${form.default_status === 'draft' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
                  Brouillon
                </button>
                <button type="button" onClick={() => setForm({ ...form, default_status: 'publish' })}
                  className={`flex-1 py-2.5 rounded-xl font-semibold border-2 ${form.default_status === 'publish' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
                  Publier
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                Nombre de catégories suggérées par l'IA
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, default_categories_count: 1 })}
                  data-testid="site-field-cats-count-1"
                  className={`flex-1 py-2.5 rounded-xl font-semibold border-2 ${Number(form.default_categories_count) === 1 ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
                  1 catégorie
                </button>
                <button type="button" onClick={() => setForm({ ...form, default_categories_count: 2 })}
                  data-testid="site-field-cats-count-2"
                  className={`flex-1 py-2.5 rounded-xl font-semibold border-2 ${Number(form.default_categories_count) === 2 ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
                  2 catégories
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                L'IA choisira au maximum ce nombre de catégories pertinentes. Vous pouvez toujours ajuster avant publication.
              </p>
            </div>

            {/* ------------------ Editorial rules ------------------ */}
            <div className="pt-4 mt-4 border-t border-slate-100">
              <h3 className="font-bold text-slate-900 mb-3">Règles éditoriales</h3>

              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Prompt éditorial du média
                </label>
                <textarea
                  rows={5}
                  value={form.prompt_template}
                  onChange={(e) => setForm({ ...form, prompt_template: e.target.value })}
                  placeholder="Exemple : ton institutionnel, analytique et informatif. Cible un lectorat averti. Mets en avant le contexte international, les implications géopolitiques. Éviter le sensationnalisme."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none text-sm"
                  data-testid="site-field-prompt"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Ce prompt définit la ligne éditoriale utilisée par l'IA à chaque génération pour ce média.
                </p>
              </div>

              <Field
                label="Ton rédactionnel"
                value={form.tone}
                onChange={(v) => setForm({ ...form, tone: v })}
                testid="site-field-tone"
                placeholder="Ex : analytique, factuel, engagé…"
              />

              <div className="mt-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Paragraphes — minimum & maximum
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold text-slate-500 mb-1">Min</div>
                    <select
                      value={form.paragraphs_min}
                      onChange={(e) => setForm({ ...form, paragraphs_min: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none text-sm font-semibold"
                      data-testid="site-field-paragraphs-min"
                    >
                      {[2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <span className="text-slate-400 mt-5">→</span>
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold text-slate-500 mb-1">Max</div>
                    <select
                      value={form.paragraphs_max}
                      onChange={(e) => setForm({ ...form, paragraphs_max: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none text-sm font-semibold"
                      data-testid="site-field-paragraphs-max"
                    >
                      {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">L'IA générera entre {form.paragraphs_min} et {form.paragraphs_max} paragraphes selon la richesse des sources.</p>
              </div>

              <div className="mt-3">
                <Field
                  label="Style de titre"
                  value={form.title_style}
                  onChange={(v) => setForm({ ...form, title_style: v })}
                  testid="site-field-title-style"
                  placeholder="Ex : factuel et informatif / accrocheur / analytique"
                />
              </div>

              <div className="mt-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Consignes particulières
                </label>
                <textarea
                  rows={3}
                  value={form.additional_instructions}
                  onChange={(e) => setForm({ ...form, additional_instructions: e.target.value })}
                  placeholder="Ex : toujours terminer par une mise en perspective ; éviter les acronymes non expliqués ; citer au moins 2 sources dans le corps."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none text-sm"
                  data-testid="site-field-instructions"
                />
              </div>

              {/* Le bloc "Sources" et le bloc "À lire également" ne sont plus jamais
                  publiés dans les articles WordPress (choix éditorial verrouillé).
                  Ils restent visibles en interne dans Auto RadKo pour vos vérifications. */}

              <div className="mt-4">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Fenêtre de fraîcheur (jours)
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={form.freshness_days}
                  onChange={(e) => setForm({ ...form, freshness_days: Number(e.target.value) })}
                  className="w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none text-sm"
                  data-testid="site-field-freshness"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Une source plus ancienne que {form.freshness_days} jours sera traitée comme contexte historique (jamais présentée comme actuelle).
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="flex-1 border-2 border-slate-200 py-3 rounded-xl font-semibold text-slate-600" data-testid="btn-cancel-site">
                Annuler
              </button>
              <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="btn-save-site">
                {saving ? <Loader2 size={16} className="animate-spin" /> : null} Enregistrer
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, type = 'text', testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
        data-testid={testid} />
    </div>
  );
}
